import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { adminApi, type AnalyticsTimeRange } from '@/lib/api'

function StatCard({
  title,
  value,
  change,
  changeLabel,
}: {
  title: string
  value: string
  change: number
  changeLabel: string
}) {
  const isPositive = change >= 0

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      <div className="mt-2 flex items-center text-xs">
        <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
          {isPositive ? '+' : ''}
          {change}%
        </span>
        <span className="ml-1 text-gray-500">{changeLabel}</span>
      </div>
    </div>
  )
}

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState<AnalyticsTimeRange>('7d')

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['analytics', 'stats', timeRange],
    queryFn: () => adminApi.analytics.getStats(timeRange),
  })

  const { data: activeUsersChart } = useQuery({
    queryKey: ['analytics', 'active-users', timeRange],
    queryFn: () => adminApi.analytics.getActiveUsersChart(timeRange),
  })

  const { data: messageCountChart } = useQuery({
    queryKey: ['analytics', 'message-count', timeRange],
    queryFn: () => adminApi.analytics.getMessageCountChart(timeRange),
  })

  const { data: walletActivityChart } = useQuery({
    queryKey: ['analytics', 'wallet-activity', timeRange],
    queryFn: () => adminApi.analytics.getWalletActivityChart(timeRange),
  })

  return (
    <div className="px-8 py-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Platform activity metrics. Only aggregate counts are displayed —
            never message content.
          </p>
        </div>

        {/* Time range selector */}
        <div className="flex space-x-1 rounded-lg bg-gray-100 p-1">
          {(['24h', '7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                timeRange === range
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Stats cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-lg bg-gray-200"
            />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Active Users"
            value={stats.activeUsers.toLocaleString()}
            change={stats.activeUsersChange}
            changeLabel="vs previous period"
          />
          <StatCard
            title="Messages (count)"
            value={stats.messageCount.toLocaleString()}
            change={stats.messageCountChange}
            changeLabel="vs previous period"
          />
          <StatCard
            title="Wallet Transactions"
            value={stats.walletTransactions.toLocaleString()}
            change={stats.walletTransactionsChange}
            changeLabel="vs previous period"
          />
          <StatCard
            title="New Registrations"
            value={stats.newRegistrations.toLocaleString()}
            change={stats.newRegistrationsChange}
            changeLabel="vs previous period"
          />
        </div>
      ) : null}

      {/* Charts */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Active Users Chart */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Daily Active Users
          </h2>
          <div className="h-72">
            {activeUsersChart ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activeUsersChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#4c6ef5"
                    strokeWidth={2}
                    dot={false}
                    name="Active Users"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Loading chart data...
              </div>
            )}
          </div>
        </div>

        {/* Message Count Chart */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Messages Sent (count only)
          </h2>
          <div className="h-72">
            {messageCountChart ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={messageCountChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    fill="#748ffc"
                    name="Messages"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Loading chart data...
              </div>
            )}
          </div>
        </div>

        {/* Wallet Activity Chart */}
        <div className="col-span-1 rounded-lg border border-gray-200 bg-white p-6 shadow-sm lg:col-span-2">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Wallet Activity
          </h2>
          <div className="h-72">
            {walletActivityChart ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={walletActivityChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sends"
                    stroke="#4c6ef5"
                    strokeWidth={2}
                    dot={false}
                    name="Sends"
                  />
                  <Line
                    type="monotone"
                    dataKey="receives"
                    stroke="#51cf66"
                    strokeWidth={2}
                    dot={false}
                    name="Receives"
                  />
                  <Line
                    type="monotone"
                    dataKey="swaps"
                    stroke="#fcc419"
                    strokeWidth={2}
                    dot={false}
                    name="Swaps"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Loading chart data...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
